import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { CacheService } from '@ngx-cache/core';
import { Subject } from 'rxjs';
import { AuthRequest, AuthResponse, AuthValidate, OktaAuth } from '../../shared/models/app';
import { User } from '../../shared/models/user';
import { CacheResolverService } from '../cache/cache-resolver.service';
import { CacheConstant } from '../cache/cache.constant';
import { AppMessages } from '../constants/app.constant';
import { OktaConstant } from '../constants/okta.constant';
import { BaseService } from './base.service';
import { OktaPkceAuthService } from './okta-pkce-auth.service';
import { UserService } from './user.service';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService implements OnDestroy {
  
  get isSigningOut() {
    return this.userSigningOut;
  }
  authenticated$: Subject<boolean> = new Subject();
  signedOut$: Subject<any> = new Subject();
  private userSigningOut = false;
  private readonly ngUnsubscribe = new Subject<void>();

  constructor(
    protected http: HttpClient,
    private oktaPkceAuth: OktaPkceAuthService,
    private userService: UserService,
    private readonly cacheService: CacheResolverService,
    private readonly cache: CacheService
  ) {
    super(http);
    this.userService.userProfileLoaded$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.onUserProfileLoaded();
    });
  }

  init() {
    this.userSigningOut = false;
  }

  onUserProfileLoaded() {
    this.authenticated$.next(true);
  }

  isAuthenticated(): Promise<AuthValidate> {
    const oktaAuth = this.cacheService.get(CacheConstant.OktaAuth) as OktaAuth;
    const user = this.cacheService.get(CacheConstant.UserAuth) as User;
    const hasToken = oktaAuth && !!oktaAuth.accessToken;
    const hasUserDetails = !!user;
    const hasAuthDetails = hasToken && hasUserDetails;
    const response: AuthValidate = {
      authenticated: {
        app: hasAuthDetails,
        okta: false,
        all: false,
      },
    };

    if (hasAuthDetails) {
      response.authenticated.okta = response.authenticated.all = true;
      return Promise.resolve(response);
    }

    return this.oktaPkceAuth
      .isAuthenticated()
      .then((oktaAuthenticated) => {
        response.authenticated.okta = oktaAuthenticated;
        response.authenticated.all = oktaAuthenticated && hasAuthDetails;

        return Promise.resolve(response);
      })
      .catch(() => {
        response.authenticated.okta = response.authenticated.all = false;
        return Promise.resolve(response);
      });
  }

  authenticate(authRequest: AuthRequest) {
    return this.oktaPkceAuth
      .authenticate(authRequest.userName, authRequest.password)
      .then((response) => {
        const authResponse: AuthResponse = {
          statusText: response.status,
          error: null,
        };

        if (response.status !== OktaConstant.AuthStatus.Success) {
          authResponse.error = this.resolveAuthError(response);
        }

        return authResponse;
      })
      .catch((error) => {
        const authResponse: AuthResponse = {
          statusText: '',
          error: OktaConstant.Messages.InvalidUserNamePassword,
        };

        return authResponse;
      });
  }

  signOut(timeOut: boolean = false) {
    this.isAuthenticated().then((authResponse) => {
      const isInternalUser = authResponse.authenticated.app;

      if (!isInternalUser && authResponse.authenticated.okta) {
        this.oktaPkceAuth.signOut().then(() => {
          this.signOutUser(timeOut, isInternalUser);
        });
      } else {
        this.signOutUser(timeOut, isInternalUser);
      }
    });
  }

  private signOutUser(timeOut: boolean, isInternalUser: boolean) {
    const redirectUrl = '/login';
    this.oktaPkceAuth.auth.validate = false;
    this.userSigningOut = true;
    this.oktaPkceAuth.signOut();
    this.signedOut$.next({
      hasError: false,
      redirect: { url: redirectUrl },
    });
    localStorage.clear();
  }

  private resolveAuthError(response) {
    let errorMessage = '';

    switch (response.status) {
      case OktaConstant.AuthStatus.PasswordExpired:
        errorMessage = OktaConstant.Messages.PasswordExpired;
        break;

      case OktaConstant.AuthStatus.UserLocked:
        errorMessage = OktaConstant.Messages.UserLocked;
        break;

      case OktaConstant.AuthStatus.AuthFailed:
        errorMessage = OktaConstant.Messages.InvalidUserNamePassword;
        break;

      default:
        errorMessage = AppMessages.GenericErrorMessage;
        break;
    }

    return errorMessage;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
